"use client";
import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import ContactForm from "./ContactForm";
import { contact, feedback, reportIssue } from "../api/misc";
import { useAuth } from "../util/auth";
import { logger } from "../util/logger";

const ContactSection = ({ type }) => {
  const auth = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [fullUrl, setFullUrl] = useState("");
  const [browserInfo, setBrowserInfo] = useState("");

  useEffect(() => {
    const origin = window.location.origin;
    setFullUrl(`${origin}${pathname}`);
    setBrowserInfo(navigator.userAgent);
  }, [pathname]);

  useEffect(() => {
    if (
      !type ||
      ![
        "feedback",
        "bug-report",
        "support",
        "get-in-touch",
        "enterprise-inquiry",
      ].includes(type)
    ) {
      router.push("/contact");
    }
  }, [router, type]);

  const handleSubmit = async (values) => {
    logger("Form values:", values);
    let accessToken;
    if (auth.user) {
      accessToken = auth.user.accessToken;
    }

    switch (type) {
      case "feedback":
        logger("Submitting feedback");
        await feedback({
          accessToken,
          values,
          browserInfo,
          route: fullUrl || pathname,
        });
        break;
      case "bug-report":
        logger("Submitting bug report");
        await reportIssue({
          accessToken,
          values,
          browserInfo,
          route: fullUrl || pathname,
        });
        break;
      case "support":
        logger("Submitting support request");
        await contact(values, browserInfo, fullUrl || pathname);
        break;
      case "get-in-touch":
        logger("Submitting get in touch request");
        await contact(values, browserInfo, fullUrl || pathname);
        break;
      case "enterprise-inquiry":
        logger("Submitting enterprise membership inquiry");
        await contact(
          { ...values, inquiry_kind: "enterprise_membership" },
          browserInfo,
          fullUrl || pathname
        );
        break;
      default:
        break;
    }
  };

  const renderContactForm = () => {
    switch (type) {
      // Feedback
      case "feedback":
        return (
          <ContactForm
            title="Feedback"
            description="We'd love to hear about your experience."
            fields={[
              {
                name: "rating",
                label: "Rating",
                type: "rating",
                required: false,
              },
              {
                name: "message",
                label: "Feedback",
                type: "textarea",
                required: true,
              },
            ]}
            onSubmit={handleSubmit}
          />
        );
      // Bug report
      case "bug-report":
        return (
          <ContactForm
            title="Bug Report"
            description="Please provide details about the bug you encountered."
            fields={[
              {
                name: "rating",
                label: "Severity",
                type: "rating",
                required: true,
              },
              {
                name: "message",
                label: "Description",
                type: "textarea",
                required: true,
              },
            ]}
            onSubmit={handleSubmit}
          />
        );
      // Support
      case "support":
        return (
          <ContactForm
            title="Support"
            description="Please provide details about your support request."
            fields={[
              { name: "name", label: "Name", type: "text", required: true },
              { name: "email", label: "Email", type: "email", required: true },
              {
                name: "issue",
                label: "Issue Type",
                type: "select",
                options: [
                  { label: "Technical", value: "technical" },
                  { label: "Billing", value: "billing" },
                  { label: "Account Management", value: "account-management" },
                  { label: "Login", value: "login" },
                  { label: "Other", value: "other" },
                ],
                required: true,
              },
              {
                name: "message",
                label: "Description",
                type: "textarea",
                required: true,
              },
            ]}
            onSubmit={handleSubmit}
          />
        );
      // Get in touch
      case "get-in-touch":
        return (
          <ContactForm
            title="Get in Touch"
            description="Whether you're an investor, innovator, open source contributor, or
            just plain curious, we'd love to hear from you!"
            fields={[
              { name: "name", label: "Name", type: "text", required: true },
              { name: "email", label: "Email", type: "email", required: true },
              {
                name: "topic",
                label: "Topic *",
                type: "select",
                options: [
                  { label: "Partnership", value: "partnership" },
                  { label: "Investment", value: "investment" },
                  { label: "STEM & Education", value: "education" },
                  { label: "Research", value: "research" },
                  { label: "Other", value: "other" },
                ],
                required: true,
              },
              {
                name: "message",
                label: "Message",
                type: "textarea",
                required: true,
              },
            ]}
            onSubmit={handleSubmit}
          />
        );
      case "enterprise-inquiry":
        return (
          <ContactForm
            title="Enterprise membership inquiry"
            description="Tell us about your organization and how you plan to use Neurorobotics Studio. Our team will follow up with pricing and onboarding options."
            fields={[
              { name: "name", label: "Full name", type: "text", required: true },
              {
                name: "email",
                label: "Work email",
                type: "email",
                required: true,
              },
              {
                name: "company",
                label: "Organization",
                type: "text",
                required: true,
              },
              {
                name: "role",
                label: "Role",
                type: "select",
                options: [
                  { label: "Engineering / R&D", value: "engineering" },
                  { label: "Research / Academia", value: "research" },
                  { label: "Executive / Leadership", value: "executive" },
                  { label: "Procurement / IT", value: "procurement" },
                  { label: "Other", value: "other" },
                ],
                required: true,
              },
              {
                name: "team_size",
                label: "Approx. team size (optional)",
                type: "select",
                required: false,
                options: [
                  { label: "Prefer not to say", value: "" },
                  { label: "1–10", value: "1-10" },
                  { label: "11–50", value: "11-50" },
                  { label: "51–200", value: "51-200" },
                  { label: "200+", value: "200-plus" },
                ],
              },
              {
                name: "message",
                label: "Goals and requirements",
                type: "textarea",
                required: true,
              },
            ]}
            onSubmit={handleSubmit}
            successMessage="Thank you for your inquiry. We will contact you soon."
          />
        );
      default:
        return null;
    }
  };

  return renderContactForm();
};

export default ContactSection;
